/**
 * The app navigator (formerly "AppNavigator" and "MainNavigator") is used for the primary
 * navigation flows of your app.
 * Generally speaking, it will contain an auth flow (registration, login, forgot password)
 * and a "main" flow which the user will use once logged in.
 */
import React, { PropsWithChildren, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Platform } from 'react-native'
import { OneSignal } from 'react-native-onesignal'
import {
  DarkTheme,
  DefaultTheme,
  getStateFromPath,
  LinkingOptions,
  NavigationContainer,
  NavigationState,
  PathConfigMap
} from '@react-navigation/native'
import { useBusiness, useIsAuthed, useSDK } from '@vatom/sdk/react'
import {
  AppRoutes,
  AppStackParamList,
  PrivateScreensConfig,
  PublicScreensConfig,
  screenConfigNonWeb,
  TabRoutes
} from '@vatom/wallet-routes'
import { theme } from '@vatom/wombo'
import * as Linking from 'expo-linking'
import { observer } from 'mobx-react-lite'
import { pathToRegexp } from 'path-to-regexp'

import { useAnalytics } from '../../hooks/useAnalytics'
import { useBusinessThemeConfig } from '../../hooks/useBusinessTheme'
import { useIsDesktop } from '../../hooks/useIsDesktop'
import { useWasVisitedStore } from '../../hooks/useLastVisitedRoute'
import logger from '../../logger'
import { navigationRef } from '../NavigationUtilities'

import { AppStack, openDeepLink } from './AppStack'

/**
 * This type allows TypeScript to know what routes are defined in this navigator
 * as well as what properties (if any) they might take when navigating to them.
 *
 * If no params are allowed, pass through `undefined`. Generally speaking, we
 * recommend using your MobX-State-Tree store(s) to keep application state
 * rather than passing state through navigation params.
 *
 * For more information, see this documentation:
 *   https://reactnavigation.org/docs/params/
 *   https://reactnavigation.org/docs/typescript#type-checking-the-navigator
 */

interface NavigationProps extends Partial<React.ComponentProps<typeof NavigationContainer>> {
  initialUrl: string | undefined
}

const initialRouteName = AppRoutes.home

export const AppNavigator = observer((props: PropsWithChildren<NavigationProps>) => {
  const { initialUrl, onStateChange, ...rest } = props
  const isAuthed = useIsAuthed()
  const sdk = useSDK()
  // const store = useStore()

  const businessInPath = window?.location?.pathname?.split('/b/')[1]?.split('/')[0] ?? null
  const { data: business } = useBusiness({
    business: businessInPath,
    businessId: businessInPath
  })

  const [isNavigationReady, setNavigationReady] = useState(false)
  const routeNameRef = useRef<string>()
  const analytics = useAnalytics()
  const isDesktop = useIsDesktop()

  const { isDark } = useBusinessThemeConfig({
    pageConfig: business?.pageConfig,
    brandConfig: business?.brandConfig
  })
  const overrideDefaultTheme = useMemo(
    () => ({
      ...DefaultTheme,
      colors: {
        ...DefaultTheme,
        background: isDesktop ? 'transparent' : theme.colors.grayCool[50]
      }
    }),
    [isDesktop]
  )
  const colorScheme = useMemo(
    () => (isDark ? DarkTheme : overrideDefaultTheme),
    [overrideDefaultTheme, isDark]
  )

  logger.info('[AppNavigator.render] initialUrl', initialUrl)
  const screensConfigCommon = isAuthed ? PrivateScreensConfig : PublicScreensConfig

  const screenConfig = Platform.select({
    web: {
      ...screensConfigCommon
    },
    native: {
      ...screensConfigCommon,
      ...screenConfigNonWeb
    }
  }) as PathConfigMap<Partial<AppStackParamList>>

  const communitiesStack = (currentRoute: any) => {
    const params = currentRoute.params
    const createStack = (withCommunities: boolean) => {
      const keyName = withCommunities ? 'Communities' : ''
      return {
        [AppRoutes[`${keyName}Room`]]: [
          {
            name: AppRoutes.BusinessProxy,
            params: {
              business: params?.business
            },
            state: {
              routes: [
                {
                  name: TabRoutes.Connect
                }
              ]
            }
          },
          currentRoute
        ],
        [AppRoutes[`${keyName}RoomMembers`]]: [
          {
            name: AppRoutes.BusinessProxy,
            params: {
              business: params?.business
            },
            state: {
              routes: [
                {
                  name: TabRoutes.Connect
                }
              ]
            }
          },
          {
            name: AppRoutes[`${keyName}Room`],
            params: {
              business: params?.business,
              spaceId: params?.spaceId
            }
          },
          currentRoute
        ],
        [AppRoutes[`${keyName}RoomNewMessage`]]: [
          {
            name: AppRoutes.BusinessProxy,
            params: {
              business: params?.business
            },
            state: {
              routes: [
                {
                  name: TabRoutes.Connect
                }
              ]
            }
          },
          {
            name: AppRoutes[`${keyName}Room`],
            params: {
              business: params?.business,
              spaceId: params?.spaceId
            }
          },
          currentRoute
        ],
        [AppRoutes[`${keyName}RoomThread`]]: [
          {
            name: AppRoutes.BusinessProxy,
            params: {
              business: params?.business
            },
            state: {
              routes: [
                {
                  name: TabRoutes.Connect
                }
              ]
            }
          },
          {
            name: AppRoutes[`${keyName}Room`],
            params: {
              business: params?.business,
              spaceId: params?.spaceId
            }
          },
          currentRoute
        ],
        [AppRoutes[`${keyName}RoomReply`]]: [
          {
            name: AppRoutes.BusinessProxy,
            params: {
              business: params?.business
            },
            state: {
              routes: [
                {
                  name: TabRoutes.Connect
                }
              ]
            }
          },
          {
            name: AppRoutes[`${keyName}Room`],
            params: {
              business: params?.business,
              spaceId: params?.spaceId
            }
          },
          {
            name: AppRoutes[`${keyName}RoomThread`],
            params: {
              business: params?.business,
              spaceId: params?.spaceId,
              messageId: params?.messageId
            }
          },
          currentRoute
        ]
      }
    }
    const stack = {
      ...createStack(true),
      ...createStack(false)
    }

    return stack[currentRoute.name]
  }

  const customStack = (currentRoute: any) => {
    const params = currentRoute.params
    const stack = {
      [AppRoutes.profileOtherUser_Business]: [
        {
          name: AppRoutes.BusinessProxy,
          params: {
            business: params?.business
          },
          state: {
            routes: [
              {
                name: TabRoutes.Connect
              }
            ]
          }
        },
        currentRoute
      ]
    }

    // @ts-expect-error this is a weird one, not really sure how to fix. Somehow we need to check if the .name is 1 of the key of stack
    return stack[currentRoute.name]
  }

  // https://reactnavigation.org/docs/configuring-links
  const linking: LinkingOptions<Partial<AppStackParamList>> = {
    prefixes: [
      /* your linking prefixes */
      Platform.select({
        web: 'http://localhost',
        ios: 'com.vatomcorp.viewer',
        android: 'com.vatom.viewer'
      }) || 'http://localhost',
      'com.vatom',
      Linking.createURL('/'),
      'https://wallet.vatom.com',
      'wallet.vatom.com://',
      'spaces.vatom.com://',
      'https://spaces.vatom.com',
      'https://wallet.localhost:3000',
      'https://wallet.vatominc.com/',
      'https://wow.vatom.com'
    ],
    /* configuration for matching screens with paths */
    config: {
      initialRouteName: initialRouteName,
      screens: screenConfig
    },
    getStateFromPath: (path, options) => {
      logger.info('getStateFromPath path', path)
      // NOTE: Platform.OS === 'web' needs to load the same AppRoutes.connect screen to catch the auth callback
      const newPath = path === '/callback' ? '/connect' : path
      const state = getStateFromPath(newPath, options)

      const isCommunityHome = pathToRegexp('/b/:business/c/:community').test(path)
      // TODO: Replace .incliudes and other weak checks with pathToRegexp
      if (
        !isCommunityHome &&
        state &&
        path.startsWith('/b/') &&
        (path.includes('/space/') || path.includes('/c/'))
      ) {
        useWasVisitedStore.setState({ isDeeplink: true })
        return {
          routes: communitiesStack(state.routes[1])
        }
      }

      if (path.startsWith('/b/')) {
        const businessOptions = options
        if (businessOptions?.initialRouteName) {
          businessOptions.initialRouteName = AppRoutes.BusinessProxy
        }
        const businessState = getStateFromPath(path, businessOptions)
        useWasVisitedStore.setState({ isDeeplink: true })
        const customState = customStack(state?.routes[1] ?? '')

        return customState
          ? {
              routes: customState
            }
          : businessState
      }

      return state
    },
    async getInitialURL() {
      // As a fallback, you may want to do the default deep link handling
      const url = await Linking.getInitialURL()
      return url
    },
    subscribe(listener) {
      const onReceiveURL = ({ url }: { url: string }) => listener(url)

      // Listen to incoming links from deep linking
      const subscription = Linking.addEventListener('url', onReceiveURL)

      // DEBUG: Test deep linking
      // setTimeout(() => {
      //   console.log('Testing deep linking...')
      //   listener(
      //     'https://wallet.vatom.com/b/sahMOa1qQR/c/!zwiZXiEStuihuAqVzB%3Avatom.com/room/!DVHVGCqtgIjZeXnzcI%3Avatom.com'
      //   )
      // }, 2000)

      // Listen to OneSignal push notifications when the app is in the foreground
      OneSignal.Notifications.addEventListener('foregroundWillDisplay', event => {
        console.log('One signal foregroundWillDisplay', event)
        event.preventDefault()
        // some async work

        // Use display() to display the notification after some async work
        event.getNotification().display()
      })
      // Listen to OneSignal push notifications when they are opened
      OneSignal.Notifications.addEventListener('click', openedEvent => {
        console.log('useOneSignalOpenHandler notification opened:', openedEvent)
        const launchUrl = openedEvent.notification?.launchURL
        console.log('useOneSignalOpenHandler launchUrl:', launchUrl)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const additionalData: any = openedEvent.notification?.additionalData
        console.log('useOneSignalOpenHandler additionalData:', additionalData)
        const deepLink = additionalData?.deepLink
        console.log('useOneSignalOpenHandler deepLink:', deepLink)

        if (launchUrl) {
          // store.linking.setLinkingUrl(launchUrl)
          listener(launchUrl)
        } else if (deepLink) {
          // store.linking.setLinkingUrl(deepLink)
          listener(deepLink)
        }
      })

      return () => {
        // Clean up the event listeners
        subscription.remove()
      }
    }
  }

  useEffect(
    () => {
      sdk.config.loadRemoteConfig()
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  const onNavigationReady = useCallback(() => {
    console.log('[Linking. AppNavigator.onNavigationReady] isAuthed', isAuthed)
    if (!isNavigationReady) {
      setNavigationReady(true)
      if (isAuthed && initialUrl) {
        openDeepLink()
      }
    }
    // Set route name
    if (navigationRef) {
      const route = navigationRef.getCurrentRoute()
      if (route?.name) {
        routeNameRef.current = route.name
      }
    }
  }, [initialUrl, isAuthed, isNavigationReady])

  const onNavigationStateChange = async (state: NavigationState) => {
    await analytics.navigationStateSubscriber(state)
  }

  return (
    <NavigationContainer
      linking={linking}
      onReady={onNavigationReady}
      // onStateChange={onStateChange}

      // @ts-expect-error not sure how to fix this
      onStateChange={onNavigationStateChange}
      // documentTitle={{
      // enabled: false
      // formatter: (options, route) => options?.title ?? route?.name
      // }}
      ref={navigationRef}
      // NOTE: We don't currently support dark mode. This will change the background color of the navigator in a way that looks like a bug.
      // theme={colorScheme === 'dark' ? DarkTheme : DefaultTheme}

      // @ts-expect-error not sure how to fix this. First idea was the Stylesheet.create but wasn't able to make it work
      theme={colorScheme}
      // theme={DefaultTheme}
      {...rest}
    >
      {isNavigationReady ? props.children : null}
    </NavigationContainer>
  )
})

export * from '@vatom/wallet-routes'
export { AppStack }
